import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, BadgeCheck, ExternalLink, Users, Film } from "lucide-react";
import AdminDialog from "../../components/admin/AdminDialog";
import SingleImageField from "../../components/common/SingleImageField";
import { imgUrl } from "../../lib/api";

const BLANK = {
  handle: "",
  display_name: "",
  bio: "",
  avatar_url: "",
  banner_url: "",
  linked_seller_id: "",
  social_handles: [],
  tags: [],
  is_verified: false,
  is_active: true,
};

const slugify = (s) =>
  (s || "").toLowerCase().replace(/[^a-z0-9._-]+/g, "-").replace(/^-|-$/g, "");

export default function AdminCreators() {
  const [rows, setRows] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(BLANK);
  const [tagsStr, setTagsStr] = useState("");
  const [handlesStr, setHandlesStr] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [{ data }, { data: sellerRows }] = await Promise.all([
        api.get("/admin/creators"),
        api.get("/sellers/admin/all").catch(() => ({ data: [] })),
      ]);
      setRows(data || []);
      // Filter to approved sellers only (and only what we need for the dropdown)
      const approved = (sellerRows || []).filter((s) => s.status === "approved")
        .map((s) => ({ id: s.id, shop_name: s.shop_name, shop_slug: s.shop_slug, status: s.status }));
      setSellers(approved);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load creators");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(BLANK);
    setTagsStr("");
    setHandlesStr("");
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      handle: row.handle,
      display_name: row.display_name,
      bio: row.bio || "",
      avatar_url: row.avatar_url || "",
      banner_url: row.banner_url || "",
      linked_seller_id: row.linked_seller_id || "",
      social_handles: row.social_handles || [],
      tags: row.tags || [],
      is_verified: Boolean(row.is_verified),
      is_active: row.is_active !== false,
    });
    setTagsStr((row.tags || []).join(", "));
    setHandlesStr((row.social_handles || []).map((h) => `${h.platform}=${h.url}`).join("\n"));
    setDialogOpen(true);
  };

  const parseHandles = (str) => {
    return str.split(/\n+/).map((line) => {
      const eq = line.indexOf("=");
      if (eq <= 0) return null;
      const platform = line.slice(0, eq).trim().toLowerCase();
      const url = line.slice(eq + 1).trim();
      if (!/^(instagram|youtube|facebook|twitter|web)$/.test(platform) || !url) return null;
      return { platform, url };
    }).filter(Boolean);
  };

  const save = async () => {
    if (!form.handle.trim() || !form.display_name.trim()) {
      toast.error("Handle and display name are required");
      return;
    }
    const payload = {
      ...form,
      handle: slugify(form.handle),
      linked_seller_id: form.linked_seller_id || null,
      tags: tagsStr.split(",").map((s) => s.trim()).filter(Boolean),
      social_handles: parseHandles(handlesStr),
    };
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/creators/${editingId}`, payload);
        toast.success("Creator updated");
      } else {
        await api.post("/admin/creators", payload);
        toast.success("Creator created");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.display_name}"? Their reels will be unlinked.`)) return;
    try {
      await api.delete(`/admin/creators/${row.id}`);
      toast.success("Creator deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-creators-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Content</div>
          <h1 className="font-display text-3xl md:text-4xl">Creators</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-2 max-w-xl">
            Creators are the authors behind your reels. Link a creator to an existing atelier seller — or keep them standalone as an influencer.
          </p>
        </div>
        <button onClick={openNew} className="kb-btn-primary" data-testid="admin-creators-new-btn">
          <Plus size={14} className="mr-2" /> Add Creator
        </button>
      </div>

      {error && (
        <div className="mb-6 border border-red-300 bg-red-50 text-red-800 p-3 text-sm">{error}</div>
      )}

      <div className="bg-white border border-[hsl(var(--kb-border))] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[hsl(var(--kb-surface-alt))] text-left">
                <th className="p-3 font-medium">Creator</th>
                <th className="p-3 font-medium">Linked atelier</th>
                <th className="p-3 font-medium">Reels</th>
                <th className="p-3 font-medium">Followers</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-[hsl(var(--kb-muted))]">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-[hsl(var(--kb-muted))]">No creators yet.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-t border-[hsl(var(--kb-border))]" data-testid={`admin-creator-row-${r.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {r.avatar_url ? (
                        <img src={imgUrl(r.avatar_url)} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[hsl(var(--kb-surface-alt))]" />
                      )}
                      <div className="min-w-0">
                        <div className="font-medium flex items-center gap-1">
                          {r.display_name}
                          {r.is_verified && <BadgeCheck size={12} className="text-[hsl(var(--kb-primary))]" />}
                        </div>
                        <a
                          href={`/creator/${r.handle}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[hsl(var(--kb-muted))] hover:underline flex items-center gap-1"
                        >
                          <ExternalLink size={10} /> @{r.handle}
                        </a>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-xs">
                    {r.linked_seller?.shop_name ? (
                      <span className="text-[hsl(var(--kb-primary))]">{r.linked_seller.shop_name}</span>
                    ) : (
                      <span className="text-[hsl(var(--kb-muted))]">—</span>
                    )}
                  </td>
                  <td className="p-3"><span className="inline-flex items-center gap-1"><Film size={12} /> {r.reel_count || 0}</span></td>
                  <td className="p-3"><span className="inline-flex items-center gap-1"><Users size={12} /> {r.follower_count || 0}</span></td>
                  <td className="p-3">
                    <span className={`inline-block text-[11px] px-2 py-0.5 ${r.is_active ? "bg-green-100 text-green-800" : "bg-neutral-200 text-neutral-600"}`}>
                      {r.is_active ? "Active" : "Hidden"}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => openEdit(r)} className="p-1 hover:text-[hsl(var(--kb-primary))]" title="Edit" data-testid={`admin-creator-edit-${r.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove(r)} className="p-1 ml-2 hover:text-red-600" title="Delete" data-testid={`admin-creator-delete-${r.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AdminDialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        title={editingId ? "Edit creator" : "Add creator"}
        subtitle="Creators"
        testid="admin-creator-dialog"
        maxWidth="max-w-2xl"
        footer={
          <>
            <button onClick={() => setDialogOpen(false)} className="kb-btn-outline" disabled={saving}>Cancel</button>
            <button onClick={save} className="kb-btn-primary" disabled={saving} data-testid="admin-creator-save-btn">
              {saving ? "Saving…" : editingId ? "Update" : "Create"}
            </button>
          </>
        }
      >
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Handle (URL)">
            <input
              type="text"
              value={form.handle}
              onChange={(e) => setForm({ ...form, handle: e.target.value })}
              onBlur={(e) => setForm((f) => ({ ...f, handle: slugify(e.target.value) }))}
              className="kb-input"
              placeholder="nayaab-atelier"
              data-testid="admin-creator-handle"
            />
          </Field>
          <Field label="Display name">
            <input
              type="text"
              value={form.display_name}
              onChange={(e) => setForm({
                ...form,
                display_name: e.target.value,
                handle: form.handle || slugify(e.target.value),
              })}
              className="kb-input"
              data-testid="admin-creator-name"
            />
          </Field>
          <Field label="Bio" className="md:col-span-2">
            <textarea
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              className="kb-input"
              rows={2}
              maxLength={600}
            />
          </Field>
          <Field label="Avatar" className="md:col-span-2 md:col-auto">
            <SingleImageField
              value={form.avatar_url}
              onChange={(key) => setForm({ ...form, avatar_url: key || "" })}
              hint="Square crop · ~512×512"
              aspect="aspect-square"
              testid="admin-creator-avatar"
            />
          </Field>
          <Field label="Banner" className="md:col-span-2 md:col-auto">
            <SingleImageField
              value={form.banner_url}
              onChange={(key) => setForm({ ...form, banner_url: key || "" })}
              hint="Wide cover image"
              aspect="aspect-[21/9]"
              testid="admin-creator-banner"
            />
          </Field>
          <Field label="Linked atelier (optional)" className="md:col-span-2">
            <select
              value={form.linked_seller_id || ""}
              onChange={(e) => setForm({ ...form, linked_seller_id: e.target.value })}
              className="kb-input"
              data-testid="admin-creator-seller"
            >
              <option value="">— Independent creator (no atelier) —</option>
              {sellers.map((s) => (
                <option key={s.id} value={s.id}>{s.shop_name}</option>
              ))}
            </select>
            {sellers.length === 0 && (
              <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">
                No approved sellers available yet. Approve a seller first to link this creator to an atelier.
              </p>
            )}
          </Field>
          <Field label="Social handles (one per line: platform=url)" className="md:col-span-2">
            <textarea
              value={handlesStr}
              onChange={(e) => setHandlesStr(e.target.value)}
              className="kb-input font-mono text-xs"
              rows={3}
              placeholder="instagram=https://instagram.com/yourhandle&#10;youtube=https://youtube.com/@yourhandle"
            />
          </Field>
          <Field label="Tags (comma separated)" className="md:col-span-2">
            <input
              type="text"
              value={tagsStr}
              onChange={(e) => setTagsStr(e.target.value)}
              className="kb-input"
              placeholder="stylist, wedding, bridal"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_verified}
              onChange={(e) => setForm({ ...form, is_verified: e.target.checked })}
              data-testid="admin-creator-verified"
            /> Verified (blue check)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              data-testid="admin-creator-active"
            /> Active
          </label>
        </div>
      </AdminDialog>
    </div>
  );
}

function Field({ label, className = "", children }) {
  return (
    <label className={`block text-sm ${className}`}>
      <div className="mb-1 text-[hsl(var(--kb-muted))] text-xs uppercase tracking-wider">{label}</div>
      {children}
    </label>
  );
}
